import { useState, useEffect, useMemo, useRef } from 'react';
import { fetchAllPageviews, fetchAllEvents } from './analytics';
import { SUPABASE_URL } from './config';

// ── Login Gate ──────────────────────────────────────────────────────────────
function AdminLogin({ onAuth }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (user === 'admin' && pass === 'ukr-treasures-stats') {
      sessionStorage.setItem('_ukr_admin', '1');
      onAuth();
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <div style={styles.loginWrap}>
      <form style={styles.loginBox} onSubmit={handleSubmit}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
        <h2 style={{ marginBottom: 4, color: '#1a1a2e' }}>Admin Access</h2>
        <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>Enter credentials to view analytics</p>
        <div style={styles.field}>
          <label style={styles.label}>Username</label>
          <input style={styles.input} type="text" value={user} onChange={e => setUser(e.target.value)} autoComplete="username" autoFocus />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <input style={styles.input} type="password" value={pass} onChange={e => setPass(e.target.value)} autoComplete="current-password" />
        </div>
        {error && <div style={styles.error}>{error}</div>}
        <button type="submit" style={{ ...styles.btn, width: '100%', marginTop: 8 }}>Sign In</button>
      </form>
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

// ── Horizontal Bar Chart ────────────────────────────────────────────────────
function BarChart({ data, labelKey, valueKey, title, maxBars = 10 }) {
  const sorted = [...data].sort((a, b) => b[valueKey] - a[valueKey]).slice(0, maxBars);
  const max = Math.max(...sorted.map(d => d[valueKey]), 1);
  return (
    <div style={styles.chartCard}>
      <h4 style={styles.chartTitle}>{title}</h4>
      <div>
        {sorted.map((d, i) => (
          <div key={i} style={styles.barRow}>
            <span style={styles.barLabel}>{d[labelKey]}</span>
            <div style={styles.barTrack}>
              <div style={{ ...styles.barFill, width: `${(d[valueKey] / max) * 100}%` }} />
            </div>
            <span style={styles.barCount}>{d[valueKey]}</span>
          </div>
        ))}
        {sorted.length === 0 && <div style={styles.empty}>No data yet</div>}
      </div>
    </div>
  );
}

// ── Daily Vertical Bar Chart ────────────────────────────────────────────────
function DailyChart({ dailyCounts, title }) {
  const max = Math.max(...dailyCounts.map(d => d.count), 1);
  return (
    <div style={styles.chartCard}>
      <h4 style={styles.chartTitle}>{title}</h4>
      <div style={styles.dailyChart}>
        {dailyCounts.map((d, i) => (
          <div key={i} style={styles.dailyCol}>
            <div style={styles.dailyCount}>{d.count || ''}</div>
            <div style={styles.dailyBarWrap}>
              <div style={{
                ...styles.dailyBar,
                height: `${Math.max((d.count / max) * 100, d.count > 0 ? 4 : 0)}%`
              }} />
            </div>
            <div style={styles.dailyLabel}>{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Visitor Map (Leaflet, loaded dynamically) ───────────────────────────────
function VisitorMap({ visitors }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!mapRef.current || visitors.length === 0) return;

    // Dynamically load Leaflet CSS + JS if not already present
    if (!window._leafletLoaded) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      window._leafletLoaded = true;
    }

    const initMap = (L) => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: true, minZoom: 2 });
      mapInstance.current = map;

      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 18
      }).addTo(map);

      const clusters = {};
      for (const v of visitors) {
        const key = `${v.lat.toFixed(2)},${v.lng.toFixed(2)}`;
        if (!clusters[key]) clusters[key] = { lat: v.lat, lng: v.lng, city: v.city, country: v.country, count: 0, sessions: new Set() };
        clusters[key].count++;
        if (v.session_id) clusters[key].sessions.add(v.session_id);
      }

      const bounds = [];
      Object.values(clusters).forEach(cl => {
        const r = Math.max(6, Math.min(22, 6 + cl.count * 2));
        const icon = L.divIcon({
          className: '',
          html: `<div style="width:${r*2}px;height:${r*2}px;border-radius:50%;background:rgba(0,87,183,0.75);border:2px solid rgba(255,215,0,0.9);display:flex;align-items:center;justify-content:center;font-size:${Math.max(10,r-2)}px;font-weight:700;color:#ffd700;box-shadow:0 2px 8px rgba(0,0,0,0.3);">${cl.count > 1 ? cl.count : ''}</div>`,
          iconSize: [r*2, r*2], iconAnchor: [r, r],
        });
        const label = [cl.city, cl.country].filter(Boolean).join(', ') || 'Unknown';
        L.marker([cl.lat, cl.lng], { icon })
          .bindPopup(`<div style="font-size:15px;font-weight:700;color:#0057b7;">${label}</div><div style="font-size:12px;color:#888;margin-top:4px;">${cl.count} visit${cl.count > 1 ? 's' : ''} · ${cl.sessions.size} session${cl.sessions.size > 1 ? 's' : ''}</div>`)
          .addTo(map);
        bounds.push([cl.lat, cl.lng]);
      });

      if (bounds.length === 1) map.setView(bounds[0], 5);
      else if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
      else map.setView([30, 0], 2);
    };

    if (window.L) {
      initMap(window.L);
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap(window.L);
      document.head.appendChild(script);
    }

    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [visitors]);

  return (
    <div style={styles.chartCard}>
      <h4 style={styles.chartTitle}>🌍 Visitor Locations</h4>
      {visitors.length === 0
        ? <div style={styles.empty}>No geo data yet</div>
        : <div ref={mapRef} style={{ height: 380, borderRadius: 6, overflow: 'hidden' }} />
      }
    </div>
  );
}

// ── Dashboard ───────────────────────────────────────────────────────────────
function AdminDashboard({ onLogout }) {
  const [pageviews, setPageviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const configured = !!SUPABASE_URL;

  useEffect(() => {
    if (!configured) { setLoading(false); return; }
    (async () => {
      try {
        const [pv, ev] = await Promise.all([fetchAllPageviews(), fetchAllEvents()]);
        setPageviews(pv);
        setEvents(ev);
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekAgo = new Date(now - 7 * 86400000).toISOString();

    const total = pageviews.length;
    const uniqueSessions = new Set(pageviews.map(p => p.session_id)).size;
    const todayViews = pageviews.filter(p => p.created_at?.startsWith(todayStr)).length;
    const weekViews = pageviews.filter(p => p.created_at >= weekAgo).length;

    const dailyMap = {};
    const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now - i * 86400000);
      const key = d.toISOString().slice(0, 10);
      const dayName = dayNames[d.getDay()];
      const monthDay = `${d.getMonth()+1}/${d.getDate()}`;
      dailyMap[key] = { date: key, label: i <= 7 ? `${dayName}\n${monthDay}` : monthDay, count: 0 };
    }
    for (const p of pageviews) {
      const d = p.created_at?.slice(0, 10);
      if (d && dailyMap[d]) dailyMap[d].count++;
    }
    const dailyCounts = Object.values(dailyMap);

    const pageCounts = {};
    for (const p of pageviews) pageCounts[p.page || '/'] = (pageCounts[p.page || '/'] || 0) + 1;
    const topPages = Object.entries(pageCounts).map(([page, count]) => ({ page, count }));

    const deviceCounts = {};
    for (const p of pageviews) deviceCounts[p.device_type || 'unknown'] = (deviceCounts[p.device_type || 'unknown'] || 0) + 1;
    const devices = Object.entries(deviceCounts).map(([device, count]) => ({ device, count }));

    const browserCounts = {};
    for (const p of pageviews) browserCounts[p.browser || 'unknown'] = (browserCounts[p.browser || 'unknown'] || 0) + 1;
    const browsers = Object.entries(browserCounts).map(([browser, count]) => ({ browser, count }));

    const refCounts = {};
    for (const p of pageviews) {
      let ref = 'Direct';
      if (p.referrer) { try { ref = new URL(p.referrer).hostname; } catch {} }
      refCounts[ref] = (refCounts[ref] || 0) + 1;
    }
    const referrers = Object.entries(refCounts).map(([source, count]) => ({ source, count }));

    const countryCounts = {};
    for (const p of pageviews) { const c = p.country || 'Unknown'; countryCounts[c] = (countryCounts[c] || 0) + 1; }
    const countries = Object.entries(countryCounts).map(([country, count]) => ({ country, count }));

    const cityCounts = {};
    for (const p of pageviews) {
      const c = [p.city, p.country_code].filter(Boolean).join(', ') || 'Unknown';
      cityCounts[c] = (cityCounts[c] || 0) + 1;
    }
    const cities = Object.entries(cityCounts).map(([city, count]) => ({ city, count }));

    const geoVisitors = pageviews.filter(p => p.lat && p.lng).map(p => ({
      lat: p.lat, lng: p.lng, city: p.city, country: p.country, session_id: p.session_id,
    }));

    const eventCounts = {};
    for (const e of events) eventCounts[e.event_name || 'unknown'] = (eventCounts[e.event_name || 'unknown'] || 0) + 1;
    const topEvents = Object.entries(eventCounts).map(([event, count]) => ({ event, count }));

    return { total, uniqueSessions, todayViews, weekViews, dailyCounts, topPages, devices, browsers, referrers, countries, cities, geoVisitors, topEvents };
  }, [pageviews, events]);

  if (!configured) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>📊 Analytics Dashboard</h2>
          <button style={styles.btnGhost} onClick={onLogout}>Sign Out</button>
        </div>
        <div style={styles.setupBox}>
          <h3>⚙️ Supabase Not Configured</h3>
          <p style={{ marginTop: 8 }}>Add your Supabase credentials to <code>src/config.js</code> to enable analytics.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>📊 Analytics Dashboard</h2>
          <button style={styles.btnGhost} onClick={onLogout}>Sign Out</button>
        </div>
        <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>Loading analytics data…</div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>📊 Analytics Dashboard</h2>
        <button style={styles.btnGhost} onClick={onLogout}>Sign Out</button>
      </div>

      {err && <div style={{ ...styles.error, marginBottom: 16 }}>Error: {err}</div>}

      <div style={styles.statsGrid}>
        <StatCard label="Total Pageviews" value={stats.total.toLocaleString()} />
        <StatCard label="Unique Sessions" value={stats.uniqueSessions.toLocaleString()} />
        <StatCard label="Today" value={stats.todayViews.toLocaleString()} />
        <StatCard label="Last 7 Days" value={stats.weekViews.toLocaleString()} />
      </div>

      <DailyChart dailyCounts={stats.dailyCounts} title="Pageviews — Last 14 Days" />

      <VisitorMap visitors={stats.geoVisitors} />

      <div style={styles.chartsRow}>
        <BarChart data={stats.countries} labelKey="country" valueKey="count" title="🌍 Countries" />
        <BarChart data={stats.cities} labelKey="city" valueKey="count" title="🏙 Cities" maxBars={8} />
      </div>

      <div style={styles.chartsRow}>
        <BarChart data={stats.topPages} labelKey="page" valueKey="count" title="📄 Top Pages" />
        <BarChart data={stats.referrers} labelKey="source" valueKey="count" title="🔗 Referrers" />
      </div>

      <div style={styles.chartsRow}>
        <BarChart data={stats.devices} labelKey="device" valueKey="count" title="📱 Devices" />
        <BarChart data={stats.browsers} labelKey="browser" valueKey="count" title="🌐 Browsers" />
      </div>

      {stats.topEvents.length > 0 && (
        <BarChart data={stats.topEvents} labelKey="event" valueKey="count" title="⚡ Events" />
      )}

      <div style={styles.chartCard}>
        <h4 style={styles.chartTitle}>Recent Visits</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Time','Page','Location','Device','Browser','Referrer'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageviews.slice(0, 30).map((p, i) => {
                const loc = [p.city, p.country_code].filter(Boolean).join(', ') || '—';
                let ref = 'Direct';
                if (p.referrer) { try { ref = new URL(p.referrer).hostname; } catch {} }
                return (
                  <tr key={i} style={i % 2 === 0 ? {} : { background: '#f9f9f9' }}>
                    <td style={styles.td}>{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                    <td style={styles.td}>{p.page || '/'}</td>
                    <td style={styles.td}>{loc}</td>
                    <td style={styles.td}>{p.device_type || '—'}</td>
                    <td style={styles.td}>{p.browser || '—'}</td>
                    <td style={{ ...styles.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref}</td>
                  </tr>
                );
              })}
              {pageviews.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>No visits recorded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  loginWrap: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loginBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '40px 36px', width: 340, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'center' },
  field: { marginBottom: 16, textAlign: 'left' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none' },
  error: { background: '#ffeaea', color: '#c0392b', padding: '8px 12px', borderRadius: 6, fontSize: 13 },
  btn: { background: '#0057b7', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost: { background: 'transparent', color: '#0057b7', border: '1px solid #0057b7', borderRadius: 6, padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  wrap: { maxWidth: 1100, margin: '0 auto', padding: '24px 20px 60px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, paddingBottom: 16, borderBottom: '2px solid #ffd700' },
  headerTitle: { fontSize: 22, fontWeight: 700, color: '#1a1a2e' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '20px 16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  statValue: { fontSize: 32, fontWeight: 700, color: '#0057b7', marginBottom: 4 },
  statLabel: { fontSize: 13, color: '#666', fontWeight: 500 },
  statSub: { fontSize: 12, color: '#aaa', marginTop: 4 },
  chartCard: { background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '20px 20px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  chartTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 0 },
  barRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  barLabel: { width: 120, fontSize: 13, color: '#444', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { flex: 1, background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(90deg, #0057b7, #ffd700)', borderRadius: 4, transition: 'width 0.4s ease' },
  barCount: { width: 36, fontSize: 12, color: '#888', textAlign: 'right', flexShrink: 0 },
  empty: { color: '#aaa', fontSize: 13, padding: '12px 0' },
  dailyChart: { display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 },
  dailyCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' },
  dailyCount: { fontSize: 10, color: '#888', marginBottom: 2 },
  dailyBarWrap: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  dailyBar: { width: '70%', background: 'linear-gradient(180deg, #0057b7, #003d82)', borderRadius: '3px 3px 0 0', minHeight: 0, transition: 'height 0.3s ease' },
  dailyLabel: { fontSize: 9, color: '#aaa', textAlign: 'center', marginTop: 4, whiteSpace: 'pre-line' },
  setupBox: { background: '#fff9e6', border: '1px solid #ffd700', borderRadius: 10, padding: 24 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', background: '#f4f4f4', color: '#555', fontWeight: 600, borderBottom: '1px solid #e0e0e0' },
  td: { padding: '8px 12px', color: '#444', borderBottom: '1px solid #f0f0f0' },
};

// ── Export ───────────────────────────────────────────────────────────────────
export default function AdminPage({ onBack }) {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('_ukr_admin') === '1');
  const handleLogout = () => { sessionStorage.removeItem('_ukr_admin'); setAuthed(false); };

  return (
    <div>
      <div style={{ background: '#0057b7', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: '#ffd700', fontWeight: 700, fontSize: 18 }}>🏛 Украдені скарби — Admin</span>
        <button style={{ ...styles.btnGhost, color: '#ffd700', borderColor: '#ffd700' }} onClick={onBack}>← Back</button>
      </div>
      {authed
        ? <AdminDashboard onLogout={handleLogout} />
        : <AdminLogin onAuth={() => setAuthed(true)} />
      }
    </div>
  );
}
